/** Registration desk: what a person still needs, and what to do next (pure, testable) */

export interface DeskParticipant {
  id: string;
  public_token: string;
  registration_status: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  payment_status: string | null;
  calculated_price: number | null;
  payment_amount: number | null;
  rejection_reason: string | null;
  registration_source: 'ADMIN' | 'PUBLIC' | 'IMPORT';
  documents: Record<string, string | undefined>;
  /** General attendance: set when the credential is handed over at the desk (or checked in elsewhere) */
  attendance_status: string | null;
  checked_in_at: string | null;
  /** Habilitado para votar. null = default (ministers and delegates yes, guests no) */
  voting_enabled?: boolean | null;
  /** Delegate guest: votes like a minister by default */
  is_delegate?: boolean;
  /** PIN to vote from the phone (printed on the badge) */
  voting_pin?: string | null;
}

export interface DeskPerson {
  minister_id: string | null;
  full_name: string | null;
  dni: string | null;
  adb_code: string | null;
  district_name: string | null;
  region_name: string | null;
  degree_name?: string | null;
}

export type DeskKind = 'MINISTER' | 'DELEGATE' | 'GUEST';

export interface DeskPricing {
  degree_id: string | null;
  district_id: string | null;
  region_id: string | null;
  birth_date: string | null;
  position: string | null;
  authority_roles: { org_type: string; role: string }[];
}

/** Contact data of a minister's record, to prefill the walk-in form */
export interface DeskContact {
  cell_phone: string | null;
  office_phone: string | null;
  email: string | null;
  address: string | null;
}

export type DeskRow =
  | { source: 'PARTICIPANT'; kind: DeskKind; participant: DeskParticipant; person: DeskPerson }
  | { source: 'MINISTER'; kind: 'MINISTER'; person: DeskPerson; pricing: DeskPricing; contact: DeskContact };

export type StepState = 'done' | 'pending' | 'blocked';
export type NextAction = 'REJECTED' | 'REVIEW' | 'CHARGE' | 'ISSUE_RECEIPT' | 'CREDENTIAL' | 'DONE';

export interface DeskStep {
  id: 'registered' | 'verified' | 'paid' | 'receipt' | 'credential';
  label: string;
  state: StepState;
}

/** Recorded payments of the person, with the receipt they were issued (if any) */
export interface DeskPayment {
  id: string;
  amount: number;
  status: 'RECORDED' | 'REVERSED';
  receipt_id: string | null;
}

export function outstandingAmount(p: Pick<DeskParticipant, 'calculated_price' | 'payment_amount'>): number {
  return Math.max(0, Math.round((Number(p.calculated_price ?? 0) - Number(p.payment_amount ?? 0)) * 100) / 100);
}

/** Free registrations (price 0) need neither payment nor receipt */
export const isFree = (p: Pick<DeskParticipant, 'calculated_price'>) => Number(p.calculated_price ?? 0) <= 0;

export function isSettled(p: DeskParticipant): boolean {
  return isFree(p) || p.payment_status === 'PAID';
}

export function paymentsMissingReceipt(payments: DeskPayment[]): DeskPayment[] {
  return payments.filter((p) => p.status === 'RECORDED' && !p.receipt_id);
}

export function getDeskSteps(p: DeskParticipant, payments: DeskPayment[]): DeskStep[] {
  const settled = isSettled(p);
  const missing = paymentsMissingReceipt(payments).length;
  const hasReceipts = payments.some((x) => x.status === 'RECORDED' && x.receipt_id);
  return [
    { id: 'registered', label: 'Inscrito', state: 'done' },
    {
      id: 'verified',
      label: 'Verificado',
      state: p.registration_status === 'VERIFIED' ? 'done' : p.registration_status === 'REJECTED' ? 'blocked' : 'pending',
    },
    { id: 'paid', label: 'Pagado', state: settled ? 'done' : 'pending' },
    {
      id: 'receipt',
      label: 'Recibo',
      state: isFree(p) ? 'done' : missing === 0 && hasReceipts ? 'done' : 'pending',
    },
    { id: 'credential', label: 'Credencial', state: p.attendance_status === 'ATTENDED' ? 'done' : 'pending' },
  ];
}

/** The one thing the desk should do now */
export function getNextAction(p: DeskParticipant, payments: DeskPayment[]): NextAction {
  if (p.registration_status === 'REJECTED') return 'REJECTED';
  if (p.registration_status === 'PRE_REGISTERED') return 'REVIEW';
  if (!isSettled(p)) return 'CHARGE';
  if (!isFree(p) && paymentsMissingReceipt(payments).length > 0) return 'ISSUE_RECEIPT';
  return p.attendance_status === 'ATTENDED' ? 'DONE' : 'CREDENTIAL';
}

// ─── Evidence collected at the desk (photos, signature, declaration) ─────────

export interface DeskEvidence {
  ci_front?: string;
  ci_back?: string;
  delegation_letter?: string;
  /** Drawn signature (required on Digital-signature events, optional otherwise) */
  signature?: Blob | null;
  declaration: boolean;
}

const EVIDENCE_LABELS = {
  ci_front: 'CI anverso',
  ci_back: 'CI reverso',
  delegation_letter: 'Carta de delegación',
} as const;

/** Photos required to register someone at the desk. Guests are not asked for them (unchanged). */
export function requiredEvidence(kind: DeskKind): (keyof typeof EVIDENCE_LABELS)[] {
  if (kind === 'MINISTER') return ['ci_front', 'ci_back'];
  if (kind === 'DELEGATE') return ['ci_front', 'ci_back', 'delegation_letter'];
  return [];
}

/**
 * What is still missing before the person can be registered.
 * `signatureRequired`: the event signs digitally, so the drawn signature is needed too.
 */
export function missingEvidence(kind: DeskKind, evidence: DeskEvidence, signatureRequired = false): string[] {
  const missing: string[] = requiredEvidence(kind)
    .filter((doc) => !evidence[doc])
    .map((doc) => EVIDENCE_LABELS[doc]);
  if (signatureRequired && !evidence.signature) missing.push('Firma');
  if (!evidence.declaration) missing.push('Aceptar la declaración');
  return missing;
}

/** Paying by transfer / QR needs the bank voucher photo for the record (cash does not) */
export function voucherRequired(method: string, hasVoucher: boolean): boolean {
  return method === 'bank_transfer' && !hasVoucher;
}
